import React, { useEffect, useState } from "react";
import { FileText, Upload, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import DiscussButton from "@/components/v2/DiscussButton";

function UploadDialog({ open, onOpenChange, onDone }) {
  const blank = () => ({
    file: null, client_name: "", quotation_no: "", project_name: "",
    quotation_date: "", total_inr: "", product_categories: "", tags: "", notes: "",
  });
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(blank()); }, [open]);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target?.files ? e.target.files[0] : e.target.value });

  const submit = async () => {
    if (!form.file) { toast.error("Pick a PDF / DOCX / XLSX"); return; }
    if (!form.client_name.trim()) { toast.error("Client name required"); return; }
    setBusy(true);
    try {
      await v2Api.imports.historicalQuotationUpload(form.file, {
        client_name: form.client_name.trim(),
        quotation_no: form.quotation_no.trim(),
        project_name: form.project_name.trim(),
        quotation_date: form.quotation_date,
        total_inr: form.total_inr ? Number(form.total_inr) : "",
        product_categories: form.product_categories.trim(),
        tags: form.tags.trim(),
        notes: form.notes.trim(),
      });
      toast.success("Uploaded");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Archive a past quotation</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2 text-sm">
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">File (PDF / DOCX / XLSX) *</Label>
            <Input type="file" accept=".pdf,.docx,.doc,.xlsx,.xls" onChange={set("file")} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Client name *</Label>
            <Input value={form.client_name} onChange={set("client_name")} placeholder="IOCL Panipat" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Quotation no.</Label>
            <Input value={form.quotation_no} onChange={set("quotation_no")} placeholder="Q-2024-0432" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Quotation date</Label>
            <Input type="date" value={form.quotation_date} onChange={set("quotation_date")} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Project / RFP name</Label>
            <Input value={form.project_name} onChange={set("project_name")} placeholder="CEMS supply for Panipat refinery" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Total (INR)</Label>
            <Input type="number" value={form.total_inr} onChange={set("total_inr")} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Tags (semicolon-separated)</Label>
            <Input value={form.tags} onChange={set("tags")} placeholder="psu;refinery;cems" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Product categories (semicolon-separated)</Label>
            <Input value={form.product_categories} onChange={set("product_categories")} placeholder="CEMS;DAS;FLOW" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-500">Notes</Label>
            <Input value={form.notes} onChange={set("notes")} placeholder="Why this quotation matters / what changed since" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? "Uploading…" : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function QuotationsArchivePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [q, setQ] = useState("");
  const refresh = async () => {
    setLoading(true);
    try {
      const data = await v2Api.imports.historicalQuotationsList(
        q ? { client: q } : undefined,
      );
      setItems(data || []);
    } catch { toast.error("Could not load archive"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const fmtSize = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };
  const fmtInr = (n) =>
    n == null ? "—" : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" /> Quotations Archive
          </h1>
          <p className="text-sm text-slate-500">
            Reference repository of past quotations (PDF / DOCX / XLSX). Drop in old
            quotations so proposal-writers can answer "what did we quote IOCL for CEMS
            in 2023?" without grepping SharePoint.
          </p>
        </div>
        <Button onClick={() => setOpenUpload(true)}>
          <Upload className="w-4 h-4 mr-1" /> Upload quotation
        </Button>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by client name…"
            className="pl-8"
            onKeyDown={(e) => { if (e.key === "Enter") refresh(); }}
          />
        </div>
        <Button variant="outline" onClick={refresh}>Search</Button>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && items.length === 0 && (
        <Card><CardContent className="py-10 text-center text-slate-400">
          No archived quotations yet. Click <em>Upload quotation</em> to drop in your first one.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {items.map((q) => (
          <Card key={q.id}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800 truncate">
                    {q.project_name || q.file_name || "(untitled)"}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {q.client_name}
                    {q.quotation_no && <> · <span className="font-mono">{q.quotation_no}</span></>}
                    {q.quotation_date && <> · {q.quotation_date}</>}
                    {q.total_inr != null && <> · ₹{fmtInr(q.total_inr)}</>}
                  </p>
                  {(q.tags?.length > 0 || q.product_categories?.length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(q.product_categories || []).map((c) => (
                        <Badge key={`c-${c}`} variant="outline" className="text-[10px]">{c}</Badge>
                      ))}
                      {(q.tags || []).map((t) => (
                        <Badge key={`t-${t}`} className="bg-amber-100 text-amber-700 text-[10px]">{t}</Badge>
                      ))}
                    </div>
                  )}
                  {q.notes && <p className="text-xs text-slate-500 mt-1 italic line-clamp-2">{q.notes}</p>}
                </div>
                <div className="text-right shrink-0 text-xs text-slate-400 space-y-0.5">
                  <p className="font-mono">{q.file_type?.toUpperCase()}</p>
                  <p>{fmtSize(q.file_size_bytes)}</p>
                </div>
                <div className="shrink-0">
                  <DiscussButton
                    entityType="historical_quotation"
                    entityId={q.id}
                    title={q.project_name || q.client_name}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <UploadDialog open={openUpload} onOpenChange={setOpenUpload} onDone={refresh} />
    </div>
  );
}
